import { useTranslation } from 'react-i18next';
import RarityBadge from '@/components/RarityBadge';
import { formatCurrency } from '@/utils/formatUtils';
import { getProductThumbnailUrl } from '@/utils/imageUtils';

// Calculate the lowest price based on user's current selection
const calculateSelectedLowestPrice = (cardStats, internalCardType, grade, condition, quantity) => {
  if (!cardStats) return null;

  if (internalCardType === 'PSA_SINGLE_CARD') {
    // For PSA cards, find the price for the selected grade
    const psaStat = cardStats.psaStats?.find(stat => stat.psa === grade);
    return psaStat?.lowestPriceOnSale || null;
  } else if (internalCardType === 'NON_PSA_SINGLE_CARD') {
    // For non-PSA cards, find the price for the selected condition
    const conditionStat = cardStats.conditionStats?.find(stat => stat.condition === condition);
    return conditionStat?.lowestPriceOnSale || null;
  } else if (internalCardType === 'CARD_BOX') {
    // For card boxes, find the price for the selected quantity
    const quantityStat = cardStats.bundledQuantityStats?.find(stat => stat.quantity === quantity);
    return quantityStat?.lowestPriceOnSale || null;
  }

  return null;
};

const CardTypeSection = ({ card, cardStats, internalCardType, grade, condition, quantity }) => {
  const { t } = useTranslation();

  // Calculate the lowest price based on user's selection
  const selectedLowestPrice = calculateSelectedLowestPrice(cardStats, internalCardType, grade, condition, quantity);

  return (
    <div className="bg-white rounded-md">
      <div className="py-6 px-6">
        <h2 className="text-[#111111] text-lg font-bold leading-[27px]">
          {t('sell.product_type.title')}
        </h2>
      </div>

      <div className="px-6 pb-6">
        <div className="flex gap-[19px]">
          <div className="w-[88px] h-[88px] bg-white rounded flex items-center justify-center">
            <img
              src={getProductThumbnailUrl(card?.images?.[0]?.url)}
              alt={card?.name}
              className="w-[50px] h-[70px] rounded-sm"
            />
          </div>

          <div className="flex-1">
            <div>
              <div className="flex items-center gap-1.5">
                <div className="text-[#14151a] text-sm">
                  {card?.localizedRawName || card?.localizedName}
                  {card?.type !== 'CARD_BOX' && card?.rarityInfo?.sub?.name ? `(${card?.rarityInfo?.sub?.name})` : ''}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1 mt-1">
                {card?.type !== 'CARD_BOX' && card?.rarityInfo?.main && (
                  <RarityBadge rarity={card?.rarityInfo.main} />
                )}
                {(card?.set?.code || (card?.rawCode && card?.type !== 'CARD_BOX')) && (
                  <div className="h-[18px] px-1 bg-[#f4f4f4] rounded-[3px] outline outline-[0.50px] outline-offset-[-0.50px] outline-[#dddddd] flex justify-start items-center gap-2.5">
                    <div className="justify-start text-[#111111] text-sm font-normal leading-[14px]">
                      {card?.type === 'CARD_BOX' ? card?.set?.code : (card?.set?.code && card?.rawCode ? `${card?.set?.code} ${card?.rawCode}` : card?.set?.code || card?.rawCode)}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-1 mt-4">
              <div className="flex gap-2">
                <div className="w-[140px] text-[#aaaaaa] text-sm leading-[21px]">
                  {t('sell.product_type.brand')}
                </div>
                <div className="flex-1 text-[#111111] text-sm leading-[21px]">
                  {card?.brand?.localizedName}
                </div>
              </div>

              <div className="flex gap-2">
                <div className="w-[140px] text-[#aaaaaa] text-sm leading-[21px]">
                  {t('sell.product_type.lowest_price')}
                </div>
                <div className="flex-1 text-[#111111] text-sm leading-[21px]">
                  {selectedLowestPrice ? formatCurrency(selectedLowestPrice) : '-'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardTypeSection; 
